import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PageHero from '../components/layout/PageHero';
import TrendingSection from '../components/trending/TrendingSection';
import { ChevronRight } from 'lucide-react';
import { TOOL_CATEGORIES, ALL_TOOLS, toolToTrendingItem } from '../lib/toolsCatalog';

const ToolCard = ({ icon: Icon, title, to, description, comingSoon }) => {
  const content = (
    <Card className={`technical-card h-full${comingSoon ? ' opacity-60' : ''}`}>
      <CardContent className="p-6 flex flex-col h-full">
        <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center mb-4">
          <Icon className="h-5 w-5 text-autoxium-blue" />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-chivo text-lg font-bold uppercase tracking-tight">{title}</h3>
          {comingSoon && <Badge variant="outline" className="font-mono text-[10px]">Coming Soon</Badge>}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
        {!comingSoon && (
          <div className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-autoxium-blue mt-4">
            Open Tool <ChevronRight className="h-3.5 w-3.5" />
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (comingSoon || !to) {
    return <div data-testid={`tool-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>{content}</div>;
  }

  return (
    <Link to={to} data-testid={`tool-card-${to.split('/').pop()}`}>
      {content}
    </Link>
  );
};

const ToolsPage = () => {
  const trendingToolItems = useMemo(() => ALL_TOOLS.map(toolToTrendingItem), []);

  return (
    <div className="min-h-screen" data-testid="tools-page">
      <PageHero
        eyebrow="Engineering Utilities"
        title="Tools"
        description="Focused calculators for automotive engineering work — AUTOSAR E2E/CRC, powertrain sizing, and vehicle dynamics."
        testId="tools-hero"
      />

      <TrendingSection sectionType="tool" items={trendingToolItems} />

      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 space-y-12">
          {TOOL_CATEGORIES.map((category) => (
            <div key={category.id} data-testid={`tool-category-${category.id}`}>
              <h2 className="font-chivo text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
                {category.title}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.tools.map((tool) => (
                  <ToolCard key={tool.title} {...tool} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ToolsPage;
